import { Doc, DocPath } from "@/components/Doc/Doc";
import { FlashOnChangeTD } from "@/components/FlashOnChange/FlashOnChange";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import { Panel } from "@/components/Panel/Panel";
import { ReturnMetricsAtoms } from "@/financeModels/owasco/returnMetrics";
import { MathResult } from "@/financeModels/types";
import { monospaceFontFamily } from "@/theme";
import { formatMoney } from "@/utils/moneyFormating";
import { Button, Group, Table } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { atom, useAtomValue } from "jotai";
import { Fragment } from "react";
import { BiChevronRight } from "react-icons/bi";
import { RiCornerDownRightLine } from "react-icons/ri";

const trueAtom = atom(true);

type HeaderItem = { label: React.ReactNode; docPath: DocPath; w?: number };

const HeaderItem = ({ header }: { header: HeaderItem }) => {
  return (
    <Table.Th w={header.w}>
      <LabelExplainer
        lh={1.8}
        label={header.label}
        explanation={<Doc path={header.docPath} />}
      />
    </Table.Th>
  );
};

export const ReturnMetricsTable = ({
  rows,
  headers,
}: {
  rows: ReturnMetricsAtoms[];
  headers: {
    title: HeaderItem;
    companyOwnership: HeaderItem;
    totalDistributions: HeaderItem;
    saleProceeds: HeaderItem;
    totalReturn: HeaderItem;
    irr: HeaderItem;
    moic: HeaderItem;
  };
}) => {
  // Resolve atom values
  //
  const values = rows
    .map((row) => ({
      title: row.title,
      // eslint-disable-next-line react-hooks/rules-of-hooks
      moic: useAtomValue(row.moic),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      irr: useAtomValue(row.irr),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      totalReturnPreferred: useAtomValue(row.totalReturnPreferred),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      totalReturnCommon: useAtomValue(row.totalReturnCommon),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      totalReturnTotal: useAtomValue(row.totalReturnTotal),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      saleProceedsCommon: useAtomValue(row.saleProceedsCommon),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      totalDistributionsTotal: useAtomValue(row.totalDistributionsTotal),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      totalDistributionsCommon: useAtomValue(row.totalDistributionsCommon),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      totalDistributionsPreferred: useAtomValue(
        row.totalDistributionsPreferred,
      ),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      commonOwnershipPercent: useAtomValue(row.commonOwnershipPercent),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      preferredOwnershipPercent: useAtomValue(row.preferredOwnershipPercent),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      equityLs: useLocalStorage({
        key: row.title + ":ReturnMetricsTable",
        defaultValue: false,
      }),
      // eslint-disable-next-line react-hooks/rules-of-hooks
      visible: useAtomValue(row.isVisible ?? trueAtom),
    }))
    .filter((r) => r.visible);

  return (
    <Group>
      <Panel p={0} w={400}>
        <Table withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <HeaderItem header={headers.title} />
              <HeaderItem header={headers.companyOwnership} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody style={{ whiteSpace: "nowrap" }}>
            {values.map((row, i) => (
              <Fragment key={i}>
                <Table.Tr>
                  <Table.Td>{row.title} </Table.Td>
                  <Table.Td>
                    <Button
                      rightSection={
                        <BiChevronRight
                          size={18}
                          style={{
                            transition: "transform 0.2s ease",
                            transform: row.equityLs[0]
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                          }}
                        />
                      }
                      variant={row.equityLs[0] ? "filled" : "outline"}
                      w={"100%"}
                      onClick={() => {
                        row.equityLs[1]((prev) => !prev);
                      }}
                      size="compact-xs"
                      styles={{
                        section: {
                          marginRight: -10,
                          marginLeft: 3,
                        },
                      }}
                    >
                      {row.equityLs[0] ? "Hide" : "Show"}
                    </Button>
                  </Table.Td>
                </Table.Tr>

                {row.equityLs[0] && (
                  <>
                    <Table.Tr bg="gray.1">
                      <Table.Td>
                        <RiCornerDownRightLine
                          size={15}
                          style={{
                            position: "relative",
                            top: 2,
                            marginRight: 5,
                          }}
                        />
                        Preferred Equity
                      </Table.Td>
                      <FlashPercentTd value={row.preferredOwnershipPercent} />
                    </Table.Tr>
                    <Table.Tr bg="gray.1">
                      <Table.Td>
                        <RiCornerDownRightLine
                          size={15}
                          style={{
                            position: "relative",
                            top: 2,
                            marginRight: 5,
                          }}
                        />
                        Common Equity
                      </Table.Td>
                      <FlashPercentTd value={row.commonOwnershipPercent} />
                    </Table.Tr>
                  </>
                )}
              </Fragment>
            ))}
          </Table.Tbody>
        </Table>
      </Panel>
      <Panel p={0} w={600}>
        <Table withColumnBorders>
          <Table.Thead>
            <Table.Tr h={38}>
              <HeaderItem header={headers.totalDistributions} />
              <HeaderItem header={headers.saleProceeds} />
              <HeaderItem header={headers.totalReturn} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody style={{ whiteSpace: "nowrap" }}>
            {values.map((row, i) => (
              <Fragment key={i}>
                <Table.Tr>
                  <FlashDollarTd value={row.totalDistributionsTotal} />
                  <FlashDollarTd value={row.saleProceedsCommon} />
                  <FlashDollarTd value={row.totalReturnTotal} />
                </Table.Tr>

                {row.equityLs[0] && (
                  <>
                    <Table.Tr bg="gray.1">
                      <FlashDollarTd value={row.totalDistributionsPreferred} />
                      <Table.Td>-</Table.Td>
                      <FlashDollarTd value={row.totalReturnPreferred} />
                    </Table.Tr>
                    <Table.Tr bg="gray.1">
                      <FlashDollarTd value={row.totalDistributionsCommon} />
                      <FlashDollarTd value={row.saleProceedsCommon} />
                      <FlashDollarTd value={row.totalReturnCommon} />
                    </Table.Tr>
                  </>
                )}
              </Fragment>
            ))}
          </Table.Tbody>
        </Table>
      </Panel>
      <Panel p={0} w={200}>
        <Table withColumnBorders>
          <Table.Thead>
            <Table.Tr h={58}>
              <HeaderItem header={headers.irr} />
              <HeaderItem header={headers.moic} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody style={{ whiteSpace: "nowrap" }}>
            {values.map((row, i) => (
              <Fragment key={i}>
                <Table.Tr>
                  <FlashPercentTd value={row.irr} />
                  <FlashOnChangeTD
                    content={row.moic?.toString()}
                    ff={monospaceFontFamily}
                    ta={"right"}
                  >
                    {Number(row.moic) > -10000 &&
                    Number(row.moic) < 10000 &&
                    Number(row.moic) !== 0
                      ? `${Number(row.moic).toFixed(2)}x`
                      : "-"}
                  </FlashOnChangeTD>
                </Table.Tr>

                {row.equityLs[0] && (
                  <>
                    <Table.Tr bg="gray.1">
                      <Table.Td>-</Table.Td>
                      <Table.Td>-</Table.Td>
                    </Table.Tr>
                    <Table.Tr bg="gray.1">
                      <Table.Td>-</Table.Td>
                      <Table.Td>-</Table.Td>
                    </Table.Tr>
                  </>
                )}
              </Fragment>
            ))}
          </Table.Tbody>
        </Table>
      </Panel>
    </Group>
  );
};

const FlashDollarTd = ({ value }: { value: number | MathResult }) => {
  return (
    <FlashOnChangeTD
      content={value?.toString()}
      ff={monospaceFontFamily}
      ta={"right"}
    >
      {value !== null ? formatMoney(Number(value)) : ""}
    </FlashOnChangeTD>
  );
};

const FlashPercentTd = ({ value }: { value: number | MathResult }) => {
  return (
    <FlashOnChangeTD
      content={value?.toString()}
      ff={monospaceFontFamily}
      ta={"right"}
    >
      {value !== null ? `${(Number(value) * 100).toFixed(2)}%` : "-"}
    </FlashOnChangeTD>
  );
};
